<template>
  <div v-if="isLoadingWorkspace" class="flex items-center gap-3 py-5 px-5">
    <Skeleton class="size-6 rounded" />
    <div class="space-y-2">
      <Skeleton class="h-4 w-[110px]" />
    </div>
  </div>
  <DropdownMenuRoot v-else v-model:open="toggleState">
    <DropdownMenuTrigger
      class="inline-flex items-center justify-between outline-none py-5 px-6 gap-3 w-full text-neutral-300 text-sm font-semibold border-b border-[#363E3F]"
      aria-label="Customise options"
    >
      <span class="inline-flex gap-3 items-center flex-0 w-[210px]">
        <Avatar
          :src="workspace?.icon_url"
          :alt="workspace?.name"
          :initials="workspace?.name?.[0]"
          size="small"
        />
        <span class="flex whitespace-nowrap overflow-hidden">
          <span class="truncate flex-1 text-left">
            {{ workspace?.name }}
          </span>
          <span
            v-if="workspaceConnections?.invitations.length > 0"
            class="ml-2 bg-error-400 text-neutral-900 text-xs p-1 rounded-md leading-none"
          >
            {{ workspaceConnections.invitations.length }}
          </span>
        </span>
      </span>
      <span>
        <svg-icon
          :width="'8'"
          :height="'8'"
          icon="app-icons/arrow-down"
          :fill="'currentColor'"
        />
      </span>
    </DropdownMenuTrigger>

    <div id="dd-switcher" />

    <DropdownMenuPortal to="#dd-switcher">
      <DropdownMenuContent
        v-if="workspaceConnections"
        class="min-w-[320px] ml-4 rounded bg-[#0D0E0E] text-neutral-300 outline-none p-2 shadow-[0px_10px_38px_-10px_rgba(22,_23,_24,_0.35),_0px_10px_20px_-15px_rgba(22,_23,_24,_0.2)] will-change-[opacity,transform] data-[side=top]:animate-slideDownAndFade data-[side=right]:animate-slideLeftAndFade data-[side=bottom]:animate-slideUpAndFade data-[side=left]:animate-slideRightAndFade flex flex-col gap-2 z-10"
        :side-offset="5"
      >
        <WorkspaceItem
          v-for="membership in workspaceConnections.memberships"
          :key="membership.id"
          :icon-url="membership.workspace.icon_url"
          :name="membership.workspace.name"
          :selected="workspace?.id === membership.workspace.id"
          :subscription-type="
            membership.workspace.current_subscription_plan?.subscription_type
          "
          :members-count="membership.workspace.members_count"
          @select="setCurrentWorkspace(membership.workspace.id)"
        />
        <WorkspaceItem
          v-for="external in filterredExternalMemberships"
          :key="`external-${external.workspace.id}`"
          :icon-url="external.workspace.icon_url"
          :name="external.workspace.name"
          :is-guest="true"
          :selected="workspace?.id === external.workspace.id"
          :subscription-type="
            external.workspace.current_subscription_plan?.subscription_type
          "
          :members-count="external.workspace.members_count"
          @select="setCurrentWorkspace(external.workspace.id)"
        />
        <WorkspaceItem
          v-for="invitation in workspaceConnections.invitations"
          :key="`invitation-${invitation.id}`"
          :icon-url="invitation.workspace.icon_url"
          :name="invitation.workspace.name"
          :is-guest="!!invitation.project_id"
          :subscription-type="
            invitation.workspace.current_subscription_plan?.subscription_type
          "
          :members-count="invitation.workspace.members_count"
          :invitation-id="invitation.id"
        />
        <hr class="border-gray-300/35" />
        <DropdownMenuItem
          class="group text-sm leading-none flex items-center py-2 px-5 relative select-none outline-none cursor-pointer data-[highlighted]:text-white data-[highlighted]:bg-[#191B1B] rounded-sm"
          @click="isDialogOpen = true"
        >
          + Create new workspace
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenuPortal>
  </DropdownMenuRoot>

  <Dialog v-model:open="isDialogOpen">
    <DialogContent>
      <DialogHeader>
        <DialogTitle> Create new workspace</DialogTitle>
      </DialogHeader>
      <div class="space-y-6 px-7 pb-6 pt-5 w-full md:max-w-[450px]">
        <DialogDescription class="text-sm font-normal">
          Workspaces are where you and other members can collaborate and share
          files.
        </DialogDescription>
        <form id="new-workspace" @submit="onSubmit">
          <fieldset class="flex flex-col gap-1 space-y-2">
            <Label for="name">Workspace name</Label>
            <Input
              v-bind="nameAttrs"
              id="name"
              v-model="name"
              placeholder="Enter workspace name"
            />
            <span class="text-error-400">{{ errors.name }}</span>
          </fieldset>
        </form>
      </div>
      <DialogFooter>
        <DialogClose as-child>
          <Button variant="ghost" :disabled="isPending">Cancel</Button>
        </DialogClose>
        <Button
          type="submit"
          :disabled="isPending || !meta.valid"
          :loading="isPending"
          form="new-workspace"
        >
          Create
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>

<script setup>
import { ref, computed } from 'vue';
import {
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuRoot,
  DropdownMenuTrigger,
} from 'radix-vue';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
  DialogHeader,
  DialogFooter,
  DialogClose,
} from '@ui/components/dialog';
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query';
import { toTypedSchema } from '@vee-validate/zod';
import { z } from 'zod';
import { useForm } from 'vee-validate';

import requests from '@vuex/requests.js';
import SvgIcon from '@ui/components/SvgIcon.vue';
import { Avatar } from '@ui/components/avatar';
import { Button } from '@ui/components/button';
import { Input } from '@ui/components/input';
import { Label } from '@ui/components/label';
import { Skeleton } from '@ui/components/skeleton';
import { useWorkspace } from '@/queries/composables.js';
import { queries } from '@/queries/index.js';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import WorkspaceItem from './WorkspaceItem.vue';

const validationSchema = toTypedSchema(
  z.object({
    name: z
      .string()
      .min(1, {
        message: 'The name field is required',
      })
      .max(64),
  })
);

const toggleState = ref(false);
const isDialogOpen = ref(false);

const queryClient = useQueryClient();

const { isLoading: isLoadingWorkspace, data: workspace } = useWorkspace();

// TODO: Add error handling and loading states
const { data: workspaceConnections } = useQuery(queries.workspace.connections);

// Remove external memberships that already exist in memberships
const filterredExternalMemberships = computed(() => {
  if (
    !workspaceConnections.value.externalMemberships ||
    !Array.isArray(workspaceConnections.value.externalMemberships)
  )
    return [];
  if (
    !workspaceConnections.value.memberships ||
    !Array.isArray(workspaceConnections.value.memberships)
  )
    return workspaceConnections.value.externalMemberships;

  return workspaceConnections.value.externalMemberships.reduce(
    (acc, currentValue) => {
      // Show workspaces only once
      if (
        !workspaceConnections.value.memberships.some(
          membership => membership.workspace_id === currentValue.workspace_id
        ) &&
        !acc.some(
          membership => membership.workspace_id === currentValue.workspace_id
        )
      ) {
        acc.push(currentValue);
      }
      return acc;
    },
    []
  );
});

const { handleSubmit, errors, defineField, resetForm, meta } = useForm({
  validationSchema,
});

const [name, nameAttrs] = defineField('name', {
  validateOnBlur: false,
});

const router = useRouter();
const store = useStore();

// TODO: Add error handling and loading states
const { isPending, mutate } = useMutation({
  mutationFn: async payload => requests.workspaces.create(payload),
  onSuccess: async data => {
    resetForm();
    store.dispatch('workspace/setWorkspace', { value: data });
    await queryClient.invalidateQueries({
      queryKey: queries.workspace.connections.queryKey,
    });
    router.push({
      name: 'workspace-projects',
      params: { workspaceHash: data.hash },
    });
    await queryClient.invalidateQueries();
    isDialogOpen.value = false;
  },
});

const { mutate: mutateSetCurrentWorkspace } = useMutation({
  mutationFn: async payload => requests.workspaces.setCurrent(payload),
  onSuccess: async data => {
    store.dispatch('workspace/setWorkspace', { value: data });
    router.push({
      name: 'workspace-projects',
      params: { workspaceHash: data.hash },
    });
    await queryClient.invalidateQueries();
  },
});

const onSubmit = handleSubmit(values => {
  mutate(values);
});

const setCurrentWorkspace = workspaceId => {
  mutateSetCurrentWorkspace({ workspaceId });
};
</script>
